/**
 * Order Confirmation Preview Parameter Composer
 *
 * Utility for building consistent preview parameters for Order Confirmation widget iframe.
 * Handles both template previews (template data overridden with user selections) and form editor previews (with real data).
 */

import {
  buildWidgetIframeSrc as buildBaseWidgetIframeSrc,
  sendSettingsToWidget,
  sendPageChangeToWidget,
  sendAutoFillTestData,
  createBaseWidgetSettings,
} from '@/js/widget/widget-preview.js';

/**
 * Compose preview parameters for the Order Confirmation widget
 * @param {Object} options - Configuration options
 * @param {Object} options.settings - The settings object (for editor previews)
 * @param {Object} options.template - The template object (for template card previews)
 * @param {string} options.organization_name - Organization name
 * @param {string} options.background_image_url - Background image URL
 * @param {string} options.side_image_url - Side image URL
 * @param {string} options.logo_url - Logo URL
 * @param {boolean} options.use_logo - Whether to show logo
 * @param {Object} options.comp_custom_fonts - Custom fonts data
 * @param {boolean} options.is_test_submission - Whether this is a test submission
 * @param {string} options.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} options.is_template_preview - Whether this is a template preview (vs settings editor)
 * @returns {Object} Composed parameters for the widget, ready to be sent to the widget via iframe messaging system
 */
export function composeOrderConfirmationPreviewParams(options = {}) {
  // eslint-disable-next-line no-console
  console.log(
    '[OC Preview Composer] composeOrderConfirmationPreviewParams called with options:',
    options
  );

  const {
    settings,
    template,
    comp_custom_fonts,
    is_test_submission = true,
    preview_mode = 'tablet-landscape',
    is_template_preview = false,
    page,
  } = options;

  let form_data;
  if (is_template_preview && template) {
    // Backend handles all interpolation (colors, fonts, and images) via /template-types handler
    form_data = template.template_overrides || template;
  } else if (settings) {
    // form editor preview: use actual settings data
    form_data = settings;
  } else {
    throw new Error('Either settings or template must be provided');
  }

  const widget_settings = createBaseWidgetSettings({
    form_id_key: 'order_confirmation_settings_id',
    form_id: settings?.order_confirmation_settings_id,
    form: form_data,
    is_test_submission,
    is_template_preview,
    preview_mode,
    page
  });

  if (comp_custom_fonts) {
    widget_settings.comp_custom_fonts = comp_custom_fonts;
  }

  // for editor previews only
  if (widget_settings.form && form_data?.organization_name) {
    widget_settings.form.organization_name = form_data.organization_name;
  }

  // eslint-disable-next-line no-console
  console.log(
    '[OC Preview Composer] Final widget_settings created (keys):',
    Object.keys(widget_settings)
  );

  return widget_settings;
}



/**
 * Build order confirmation widget iframe source URL
 * @param {string} preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} is_development - Whether in development mode
 * @returns {string} Widget iframe source URL
 */
export function buildWidgetIframeSrc(preview_mode = 'tablet-landscape', is_development = false) {
  return buildBaseWidgetIframeSrc({
    widget_name: 'order-confirmation',
    preview_mode,
    is_development,
    dev_port: 5176,
  });
}

export { sendSettingsToWidget, sendPageChangeToWidget, sendAutoFillTestData };
